import { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  ListSubheader,
  Divider,
} from "@mui/material";
import { useSchemes } from "../../../contexts/SchemesContext";
import { OffensiveGamePlan, GameplanPlay, OffensivePlaybookPlay } from "../../../types/SchemesTypes";
import { useSchedule } from "../../../contexts/ScheduleContext";

interface CreateGameplanModalProps {
  open: boolean;
  onClose: () => void;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 1,
};

export const CreateGameplanModal = ({
  open,
  onClose,
}: CreateGameplanModalProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedGameId, setSelectedGameId] = useState<number | "">("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const { games } = useSchedule();
  const { 
    offensiveGamePlans, 
    offensivePlaybookPlays, 
    saveOffensiveGamePlan,
    generateGameplanPlays 
  } = useSchemes();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim() || !selectedGameId) return;

    const selectedGame = games.find((game) => game.id === selectedGameId);
    if (!selectedGame) return;

    let plays: OffensivePlaybookPlay[] = [];

    // Handle different template selections
    if (selectedTemplateId === "all_plays") {
      // Generate plays from the entire playbook and convert to GameplanPlay type
      plays = generateGameplanPlays(offensivePlaybookPlays);
    } else if (selectedTemplateId !== "empty") {
      // If a specific template is selected, get its plays
      const selectedTemplate = offensiveGamePlans.find(
        plan => plan.id.toString() === selectedTemplateId
      );
      if (selectedTemplate) {
        plays = selectedTemplate.plays;
      }
    }
    // If selectedTemplateId is "empty", plays remains an empty array

    saveOffensiveGamePlan({
      name,
      description,
      game: selectedGame,
      plays,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="create-gameplan-modal"
    >
      <Box sx={modalStyle}>
        <Typography variant="h6" component="h2" gutterBottom>
          Create New Game Plan
        </Typography>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              required
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <FormControl fullWidth required>
              <InputLabel id="game-select-label">Game</InputLabel>
              <Select
                labelId="game-select-label"
                value={selectedGameId}
                label="Game"
                onChange={(e) =>
                  setSelectedGameId(e.target.value as number)
                }
              >
                {games.map((game) => (
                  <MenuItem key={game.id} value={game.id}>
                    {game.opponent_club?.name || "Unknown Opponent"} - {new Date(game.date).toLocaleDateString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel id="template-select-label">Select Plays</InputLabel>
              <Select
                labelId="template-select-label"
                value={selectedTemplateId}
                label="Select Plays"
                onChange={(e) =>
                  setSelectedTemplateId(e.target.value as string)
                }
              >
                <MenuItem value="empty" sx={{ 
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }}>
                  Create Empty Gameplan
                </MenuItem>
                <MenuItem value="all_plays" sx={{ 
                  color: 'primary.main',
                  fontWeight: 'medium'
                }}>
                  Create Gameplan with Entire Playbook
                </MenuItem>
                <Divider />
                {offensiveGamePlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id.toString()}>
                    {plan.name} - {plan.plays.length} plays
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={
                  !name.trim() ||
                  !selectedGameId ||
                  !selectedTemplateId
                }
              >
                Create
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
};
